import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Sparkles, TrendingUp } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { formatMKD } from "@/lib/mk-salary";
import { RATES } from "@/lib/mk-salary";
import { ADJ_TYPES } from "./payroll-constants";
import type { PayrollEntry, PayrollTotals } from "@/lib/api";

export function StatCard({
  label,
  value,
  subtitle,
  icon: Icon,
  gradient,
}: {
  label: string;
  value: string;
  subtitle?: string;
  icon: any;
  gradient: string;
}) {
  return (
    <Card className="shadow-card group hover:shadow-lg transition-shadow">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="text-xl font-bold text-foreground tabular-nums">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
          {subtitle && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="shadow-card">
      <CardContent className="p-3 text-center">
        <p className="text-lg font-bold text-foreground tabular-nums">{value}</p>
        <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}

export function SalaryBreakdownTable({ entry, t }: { entry: PayrollEntry; t: any }) {
  const rows = [
    { label: t("payroll.gross"), value: entry.grossSalary, bold: true, pct: "" },
    { label: t("payroll.pension") + ` (${(RATES.pension * 100).toFixed(1)}%)`, value: entry.pensionContribution, pct: `${(RATES.pension * 100).toFixed(1)}%` },
    { label: t("payroll.health") + ` (${(RATES.health * 100).toFixed(1)}%)`, value: entry.healthContribution, pct: `${(RATES.health * 100).toFixed(1)}%` },
    { label: t("payroll.unemployment") + ` (${(RATES.unemployment * 100).toFixed(1)}%)`, value: entry.unemploymentContribution, pct: `${(RATES.unemployment * 100).toFixed(1)}%` },
    { label: t("payroll.sickness") + ` (${(RATES.sickness * 100).toFixed(1)}%)`, value: entry.sicknessContribution, pct: `${(RATES.sickness * 100).toFixed(1)}%` },
    { label: t("payroll.contributions"), value: entry.totalContributions, bold: true, pct: "" },
    { label: t("payroll.grossMinusContributions"), value: entry.grossMinusContributions, pct: "" },
    { label: t("payroll.personalExemption"), value: entry.personalExemption, pct: "" },
    { label: t("payroll.taxBase"), value: entry.taxBase, pct: "" },
    { label: t("payroll.incomeTax"), value: entry.personalIncomeTax, pct: "10%" },
    { label: t("payroll.deductions"), value: entry.totalDeductions, bold: true, pct: "" },
    { label: t("payroll.net"), value: entry.netSalary, bold: true, highlight: true, pct: "" },
  ];

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={`border-b last:border-0 ${row.highlight ? "bg-primary/5" : i % 2 === 0 ? "bg-muted/30" : ""}`}>
              <td className={`px-4 py-2.5 ${row.bold ? "font-semibold" : ""} ${row.highlight ? "text-primary font-bold" : "text-foreground"}`}>{row.label}</td>
              <td className="px-4 py-2.5 text-right text-muted-foreground text-xs w-16">{row.pct}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums w-32 ${row.bold ? "font-semibold" : ""} ${row.highlight ? "text-primary font-bold" : "text-foreground"}`}>{formatMKD(row.value)}</td>
              <td className="px-2 py-2.5 text-right text-xs text-muted-foreground w-10">MKD</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TotalsBreakdownTable({ totals, t }: { totals: PayrollTotals; t: any }) {
  const rows = [
    { label: t("payroll.totalGross"), value: totals.totalGross, bold: true },
    { label: t("payroll.pension"), value: totals.totalPension },
    { label: t("payroll.health"), value: totals.totalHealth },
    { label: t("payroll.unemployment"), value: totals.totalUnemployment },
    { label: t("payroll.sickness"), value: totals.totalSickness },
    { label: t("payroll.contributions"), value: totals.totalContributions, bold: true },
    { label: t("payroll.incomeTax"), value: totals.totalTax },
    { label: t("payroll.totalDeductions"), value: totals.totalDeductions, bold: true },
    { label: t("payroll.totalNet"), value: totals.totalNet, bold: true, highlight: true },
  ];

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={`border-b last:border-0 ${(row as any).highlight ? "bg-primary/5" : i % 2 === 0 ? "bg-muted/30" : ""}`}>
              <td className={`px-4 py-3 ${row.bold ? "font-semibold" : ""} ${(row as any).highlight ? "text-primary font-bold text-base" : "text-foreground"}`}>{row.label}</td>
              <td className={`px-4 py-3 text-right tabular-nums w-40 ${row.bold ? "font-semibold" : ""} ${(row as any).highlight ? "text-primary font-bold text-base" : "text-foreground"}`}>{formatMKD(row.value)} MKD</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdjustmentsSummaryTable({ entries, t }: { entries: PayrollEntry[]; t: any }) {
  const rows = entries.flatMap((entry) =>
    (entry.adjustments || []).map((adj: any) => ({
      employeeName: entry.employeeName || "—",
      type: adj.type as string,
      name: adj.name as string,
      applyOn: adj.applyOn as string,
      amount: adj.amount as number,
      notes: adj.notes as string | null,
    })),
  );

  if (rows.length === 0) return null;

  return (
    <Card className="shadow-card animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-500" />
          {t("payroll.adjustments")}
        </CardTitle>
        <CardDescription>
          {rows.length} {t("payroll.adjustments").toLowerCase()} {t("payroll.acrossEmployees")}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{t("payroll.employee")}</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{t("payroll.adjType")}</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{t("payroll.adjName")}</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{t("payroll.appliedOn")}</th>
                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">{t("payroll.amount")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const adjCfg = ADJ_TYPES.find((a) => a.value === row.type) || ADJ_TYPES[4];
                const AdjIcon = adjCfg.icon;
                const isPositive = row.type === "bonus" || row.type === "on_call";
                return (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2.5 font-medium text-foreground">{row.employeeName}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1.5 ${adjCfg.color}`}>
                        <AdjIcon className="h-3.5 w-3.5" />
                        {t(adjCfg.labelKey)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-foreground">
                      {row.name}
                      {row.notes && <span className="text-muted-foreground text-xs ml-2">({row.notes})</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs rounded-full px-2 py-0.5 bg-muted text-muted-foreground font-medium">
                        {row.applyOn === "gross" ? t("payroll.onGross") : t("payroll.onNet")}
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${isPositive ? "text-emerald-600" : "text-red-600"}`}>
                      {isPositive ? "+" : "-"}{formatMKD(row.amount)} MKD
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export function SalaryDistribution({ entries, t }: { entries: PayrollEntry[]; t: any }) {
  const sorted = [...entries].sort((a, b) => (a.finalNetSalary || a.netSalary) - (b.finalNetSalary || b.netSalary));
  const barData = sorted.map((e) => ({
    name: e.employeeName?.split(" ")[0] || "?",
    net: e.finalNetSalary || e.netSalary,
    gross: e.grossSalary + (e.totalAdjustmentsGross || 0),
    contributions: e.totalContributions,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={barData}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
        <Tooltip
          formatter={(value: number, name: string) => [`${formatMKD(value)} MKD`, name]}
          contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }}
        />
        <Legend />
        <Bar dataKey="net" name={t("payroll.net")} stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} animationDuration={1200} />
        <Bar dataKey="contributions" name={t("payroll.contributions")} stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} animationDuration={1200} />
      </BarChart>
    </ResponsiveContainer>
  );
}
